import { useState } from 'react';
import { api } from '../lib/api.js';
import { Field } from './Field.jsx';
import { inputStyle } from '../lib/ui.js';
import { todayStr } from '../lib/format.js';

export function TORSubmitModal({ currentUser, ptoBalance, onClose, onSubmitted }) {
  const today = todayStr();
  // CJ feedback 2026-05-14: time-off requests open 180 days into the
  // future so staff can plan vacations a season ahead. Past-date and
  // beyond-window rejects happen client-side AND server-side.
  const maxFwd = (() => {
    const d = new Date(); d.setUTCDate(d.getUTCDate() + 180);
    return d.toISOString().slice(0, 10);
  })();
  const [form, setForm] = useState({
    requested_date_start: today,
    requested_date_end: today,
    full_day: true,
    requested_time_start: '09:00',
    requested_time_end: '13:00',
    use_pto: false,
    pto_hours: 8,
    reason: '',
  });
  const [submitting, setSubmitting] = useState(false);
  const [err, setErr] = useState('');

  // W-004 gating: disable Submit whenever a validation rule the server
  // will reject is currently violated. Mirrors the server check so the
  // user never sees a confusing 400 after pressing Submit.
  const pastDate = form.requested_date_start < today;
  const beyondWindow = form.requested_date_start > maxFwd;
  const ptoOverage = form.use_pto && Number(form.pto_hours) > Number(ptoBalance || 0) + 0.01;
  const blocked = pastDate || beyondWindow || ptoOverage;

  async function submit() {
    if (blocked) {
      setErr(pastDate ? 'Time off cannot be requested for past dates.'
            : beyondWindow ? 'Time off can only be requested up to 180 days in the future.'
            : 'Requested PTO exceeds your balance.');
      return;
    }
    setSubmitting(true); setErr('');
    try {
      const payload = {
        employee_id: currentUser.id,
        requested_date_start: form.requested_date_start,
        requested_date_end: form.requested_date_end,
        requested_time_start: form.full_day ? null : form.requested_time_start,
        requested_time_end: form.full_day ? null : form.requested_time_end,
        use_pto: !!form.use_pto,
        pto_hours: form.use_pto ? Number(form.pto_hours) : null,
        reason: form.reason || null,
      };
      const r = await api('POST', '/api/tor', payload, { 'x-user-id': String(currentUser.id) });
      onSubmitted(r);
    } catch (e) {
      let msg = e.message || 'Submit failed';
      try { const p = JSON.parse(msg); msg = p.error + (p.details ? ': ' + p.details.join(', ') : ''); } catch {}
      setErr(msg);
    }
    finally { setSubmitting(false); }
  }

  return (
    <div onClick={onClose}
      style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.4)', zIndex: 1000,
                display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 16 }}>
      <div onClick={(e) => e.stopPropagation()}
        style={{ background: '#fff', borderRadius: 10, padding: 20, maxWidth: 480, width: '100%' }}>
        <div style={{ fontSize: 16, fontWeight: 700, marginBottom: 12 }}>Request time off</div>
        <div style={{ display: 'grid', gap: 10 }}>
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 8 }}>
            <Field label="From">
              <input type="date" value={form.requested_date_start}
                onChange={(e) => setForm((f) => ({ ...f, requested_date_start: e.target.value, requested_date_end: e.target.value > f.requested_date_end ? e.target.value : f.requested_date_end }))}
                min={today} max={maxFwd}
                style={inputStyle} />
            </Field>
            <Field label="To">
              <input type="date" value={form.requested_date_end}
                onChange={(e) => setForm((f) => ({ ...f, requested_date_end: e.target.value }))}
                min={form.requested_date_start} max={maxFwd} style={inputStyle} />
            </Field>
          </div>
          <label style={{ display: 'flex', alignItems: 'center', gap: 6, fontSize: 13 }}>
            <input type="checkbox" checked={form.full_day}
              onChange={(e) => setForm((f) => ({ ...f, full_day: e.target.checked }))} />
            Full day(s)
          </label>
          {!form.full_day && (
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 8 }}>
              <Field label="Off from">
                <input type="time" step="1800" value={form.requested_time_start}
                  onChange={(e) => setForm((f) => ({ ...f, requested_time_start: e.target.value }))}
                  style={inputStyle} />
              </Field>
              <Field label="Off until">
                <input type="time" step="1800" value={form.requested_time_end}
                  onChange={(e) => setForm((f) => ({ ...f, requested_time_end: e.target.value }))}
                  style={inputStyle} />
              </Field>
            </div>
          )}
          <div style={{ padding: 10, background: '#fafbfc', border: '1px solid #e0e0e0', borderRadius: 6 }}>
            <label style={{ display: 'flex', alignItems: 'center', gap: 6, fontSize: 13 }}>
              <input type="checkbox" checked={form.use_pto}
                onChange={(e) => setForm((f) => ({ ...f, use_pto: e.target.checked }))} />
              Use my PTO bucket (paid)
            </label>
            <div style={{ fontSize: 11, color: '#757575', marginTop: 4 }}>
              Current balance: <strong style={{ color: '#212121' }}>
                {(Number(ptoBalance) || 0).toFixed(1)} hrs
              </strong>
            </div>
            {form.use_pto && (
              <div style={{ marginTop: 8 }}>
                <Field label="Hours to deduct">
                  <input type="number" min="0" step="0.5" max={Number(ptoBalance) || 0}
                    value={form.pto_hours}
                    onChange={(e) => setForm((f) => ({ ...f, pto_hours: e.target.value }))}
                    style={inputStyle} />
                </Field>
                {Number(form.pto_hours) > Number(ptoBalance || 0) && (
                  <div style={{ fontSize: 11, color: '#c62828', marginTop: 4 }}>
                    Requested {form.pto_hours}h exceeds your {(Number(ptoBalance) || 0).toFixed(1)}h balance.
                  </div>
                )}
              </div>
            )}
          </div>
          <Field label="Reason (optional)">
            <input value={form.reason}
              onChange={(e) => setForm((f) => ({ ...f, reason: e.target.value }))}
              placeholder="e.g. family event, medical" style={inputStyle} />
          </Field>
        </div>
        {err && <div style={{ fontSize: 11, color: '#c62828', marginTop: 6 }}>{err}</div>}
        {!err && blocked && (
          <div style={{ fontSize: 11, color: '#c62828', marginTop: 6 }}>
            {pastDate ? 'Time off cannot be requested for past dates.'
              : beyondWindow ? 'Time off can only be requested up to 180 days in the future.'
              : 'Requested PTO exceeds your balance.'}
          </div>
        )}
        <div style={{ marginTop: 16, display: 'flex', justifyContent: 'flex-end', gap: 8 }}>
          <button onClick={onClose} className="btn-secondary" style={{ fontSize: 12 }}>Cancel</button>
          <button onClick={submit} disabled={submitting || blocked}
            title={blocked ? 'Fix the issue above before submitting' : ''}
            style={{ background: blocked ? '#bdbdbd' : 'var(--forest)', color: '#fff', border: 'none', borderRadius: 6,
                      padding: '6px 16px', fontSize: 13, fontWeight: 600, cursor: blocked ? 'not-allowed' : 'pointer',
                      opacity: submitting ? 0.5 : 1 }}>
            {submitting ? 'Submitting…' : 'Submit'}
          </button>
        </div>
      </div>
    </div>
  );
}
